import { Box, Button, Flex } from "@chakra-ui/react";
import { TextFieldGroup } from "../../fields/TextFieldGroup";
import { useState } from "react";
import { t } from "i18next";
import { useSignupMutation } from "../../../hooks/useAuthMutations";
import { FunnelOnSuccess } from "../../funnel/types";

export interface SignUpWithEmailPasswordFormProps {
  onSuccess: FunnelOnSuccess;
  invalidate?: boolean;
  loading?: boolean;
  loadingText?: string;
  autoFocus?: boolean;
}

export function SignUpWithEmailPasswordForm({
  onSuccess,
  invalidate = true,
  loading = false,
  loadingText,
  autoFocus = true,
}: SignUpWithEmailPasswordFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const { mutate, isPending } = useSignupMutation({
    invalidate,
    onSuccess,
    onError: (error: any) => {
      // Handle form field errors
      if (error?.response?.data?.payload?.form_fields) {
        const fields = error.response.data.payload.form_fields;
        const emailField = fields.find((f: any) => f.name === "email");
        const passwordField = fields.find(
          (f: any) => f.name === "password_hash",
        );

        setEmailError(emailField?.error || "");
        setPasswordError(passwordField?.error || "");
      }
    },
  });

  const validatePassword = () => {
    const numberRegex = /[0-9]/;
    const specialCharRegex = /[!@#$%^&*?.]/;
    let isValid = true;

    if (password.length < 8) {
      setPasswordError(t("components.auth.passwordMustBeAtLeast8Characters"));
      isValid = false;
    } else if (!numberRegex.test(password)) {
      setPasswordError(t("components.auth.passwordMustContainNumbers"));
      isValid = false;
    } else if (!specialCharRegex.test(password)) {
      setPasswordError(t("components.auth.passwordMustContainOneSymbol"));
      isValid = false;
    } else {
      setPasswordError("");
    }
    return isValid;
  };

  const validateEmail = () => {
    let isValidEmail = true;
    if (!email) {
      setEmailError(t("components.auth.emailIsRequired"));
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isValidEmail = emailRegex.test(email);
      if (!isValidEmail) {
        setEmailError(t("components.auth.invalidEmailFormat"));
      } else {
        setEmailError("");
      }
    }

    return isValidEmail;
  };

  const handleSignUp = () => {
    const isValidEmail = validateEmail();

    const isValidPassword = validatePassword();

    if (isValidEmail && isValidPassword) {
      mutate({ email, password });
    }
  };

  return (
    <Flex
      direction="column"
      width="100%"
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          handleSignUp();
        }
      }}
    >
      <TextFieldGroup
        textFields={[
          {
            id: "inputSignupEmail",
            placeholder: t("components.auth.enterYourEmail"),
            type: "text",
            onChange: setEmail,
            error: emailError,
            autoFocus,
          },
          {
            id: "inputSignupPassword",
            placeholder: t("components.auth.createAPassword"),
            type: "password",
            onChange: setPassword,
            error: passwordError,
          },
        ]}
      />
      <Box height="24px" />
      <Button
        id="btnSignupSubmit"
        variant="primary"
        width="full"
        size="lg"
        onClick={handleSignUp}
        isLoading={loading || isPending}
        loadingText={loadingText || (loading ? "Processing..." : undefined)}
      >
        {t("components.auth.createAccount")}
      </Button>
    </Flex>
  );
}
